/**
 * Appointments Page - Zentrale Terminverwaltung
 * 
 * Features:
 * - Alle Termine firmenweit anzeigen
 * - Filter: heute, morgen, diese Woche, alle, abgesagt, abgeschlossen
 * - Suche nach Kundenname
 * - Termine bearbeiten, verschieben, absagen
 * - Automatische Update-Mail bei Terminänderung
 * - Audit Trail / Historie
 */

import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Calendar,
  Search,
  RefreshCw,
  Edit,
  Trash2,
  XCircle,
  CheckCircle,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Eye,
  History,
  Send,
  CalendarDays,
  CalendarCheck,
  CalendarX,
  AlertCircle,
  MoreHorizontal,
  ExternalLink,
  Home,
  Bell,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ReminderStatusBadge } from "../components/AppointmentReminder";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Appointment type configurations
const APPOINTMENT_TYPE_CONFIG = {
  onsite: { label: "Vor-Ort-Termin", icon: Home, color: "bg-blue-100 text-blue-800 border-blue-300" },
  phone: { label: "Telefontermin", icon: Phone, color: "bg-amber-100 text-amber-800 border-amber-300" },
};

// Filter configurations
const FILTER_OPTIONS = [
  { value: "upcoming", label: "Kommende Termine", icon: Calendar },
  { value: "today", label: "Heute", icon: CalendarDays },
  { value: "tomorrow", label: "Morgen", icon: CalendarDays },
  { value: "week", label: "Diese Woche", icon: CalendarDays },
  { value: "all", label: "Alle Termine", icon: Calendar },
  { value: "cancelled", label: "Abgesagt", icon: CalendarX },
  { value: "completed", label: "Abgeschlossen", icon: CalendarCheck },
];

// Status configurations
const STATUS_CONFIG = {
  scheduled: { label: "Geplant", color: "bg-blue-100 text-blue-800", icon: Clock },
  confirmed: { label: "Bestätigt", color: "bg-green-100 text-green-800", icon: CheckCircle },
  rescheduled: { label: "Verschoben", color: "bg-orange-100 text-orange-800", icon: RefreshCw },
  cancelled: { label: "Abgesagt", color: "bg-red-100 text-red-800", icon: XCircle },
  completed: { label: "Abgeschlossen", color: "bg-gray-100 text-gray-800", icon: CheckCircle },
};

// Page size options
const PAGE_SIZE_OPTIONS = [10, 25, 50];

export default function AppointmentsPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter and pagination state
  const initialFilter = searchParams.get("filter") || "upcoming";
  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialPageSize = parseInt(searchParams.get("pageSize") || "25", 10);
  
  const [filter, setFilter] = useState(initialFilter);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS.includes(initialPageSize) ? initialPageSize : 25);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingAppointment, setCancellingAppointment] = useState(null);
  const [sendCancellationEmail, setSendCancellationEmail] = useState(true);
  const [cancellationReason, setCancellationReason] = useState("");
  
  // Detail/History dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentEvents, setAppointmentEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAppointment, setDeletingAppointment] = useState(null);
  
  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        filter,
        page: currentPage.toString(),
        page_size: pageSize.toString(),
      });
      
      if (searchQuery) {
        params.append("search", searchQuery);
      }
      
      const response = await fetch(`${API_URL}/api/appointments/?${params}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Fehler beim Laden");
      
      const data = await response.json();
      setAppointments(data.appointments || []);
      setTotalCount(data.total_count || 0);
    } catch (err) {
      toast.error("Fehler beim Laden der Termine");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, filter, currentPage, pageSize, searchQuery]);
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/appointments/stats`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, [token]);
  
  useEffect(() => {
    fetchAppointments();
    fetchStats();
  }, [fetchAppointments, fetchStats]);
  
  // Update URL params
  const updateUrlParams = (newFilter, newPage, newPageSize) => {
    const params = new URLSearchParams();
    if (newFilter !== "upcoming") params.set("filter", newFilter);
    if (newPage !== 1) params.set("page", newPage.toString());
    if (newPageSize !== 25) params.set("pageSize", newPageSize.toString());
    setSearchParams(params);
  };
  
  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    updateUrlParams(newFilter, 1, pageSize);
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    updateUrlParams(filter, newPage, pageSize);
  };
  
  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    const size = parseInt(newSize, 10);
    setPageSize(size);
    setCurrentPage(1);
    updateUrlParams(filter, 1, size);
  };
  
  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAppointments();
  };
  
  // Open edit dialog
  const openEditDialog = (appointment) => {
    setEditingAppointment(appointment);
    setEditForm({
      date: appointment.date || "",
      time: appointment.time || "",
      duration_minutes: appointment.duration_minutes || 60,
      location_note: appointment.location_note || "",
      message: appointment.message || "",
      appointment_title: appointment.appointment_title || "",
      appointment_type: appointment.appointment_type || "onsite",
    });
    setEditDialogOpen(true);
  };
  
  // Save appointment changes
  const handleSaveAppointment = async () => {
    if (!editingAppointment) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments/${editingAppointment.id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(editForm)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Fehler beim Speichern");
      }
      
      const result = await response.json();
      
      if (result.email_sent) {
        toast.success(result.message);
      } else if (result.email_sent === false) {
        toast.warning(result.message);
      } else {
        toast.success("Termin aktualisiert");
      }
      
      setEditDialogOpen(false);
      setEditingAppointment(null);
      fetchAppointments();
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Open cancel dialog
  const openCancelDialog = (appointment) => {
    setCancellingAppointment(appointment);
    setSendCancellationEmail(true);
    setCancellationReason("");
    setCancelDialogOpen(true);
  };
  
  // Cancel appointment
  const handleCancelAppointment = async () => {
    if (!cancellingAppointment) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments/${cancellingAppointment.id}/cancel`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          send_cancellation_email: sendCancellationEmail,
          cancellation_reason: cancellationReason || null
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Fehler beim Absagen");
      }
      
      const result = await response.json();
      toast.success(result.message);
      
      setCancelDialogOpen(false);
      setCancellingAppointment(null);
      fetchAppointments();
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Complete appointment
  const handleCompleteAppointment = async (appointment) => {
    try {
      const response = await fetch(`${API_URL}/api/appointments/${appointment.id}/complete`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Fehler");
      
      toast.success("Termin als abgeschlossen markiert");
      fetchAppointments();
      fetchStats();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };
  
  // Delete appointment
  const handleDeleteAppointment = async () => {
    if (!deletingAppointment) return;
    
    try {
      const response = await fetch(`${API_URL}/api/appointments/${deletingAppointment.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Fehler beim Löschen");
      
      toast.success("Termin gelöscht");
      setDeleteDialogOpen(false);
      setDeletingAppointment(null);
      fetchAppointments();
      fetchStats();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };
  
  // Resend confirmation
  const handleResendConfirmation = async (appointment) => {
    try {
      const response = await fetch(`${API_URL}/api/appointments/${appointment.id}/resend-confirmation`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error("Fehler beim Senden");
    }
  };
  
  // Open detail dialog with events
  const openDetailDialog = async (appointment) => {
    setSelectedAppointment(appointment);
    setDetailDialogOpen(true);
    setLoadingEvents(true);
    
    try {
      const response = await fetch(`${API_URL}/api/appointments/${appointment.id}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedAppointment(data);
        setAppointmentEvents(data.events || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEvents(false);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const weekdays = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
      return `${weekdays[date.getDay()]}, ${date.toLocaleDateString("de-DE")}`;
    } catch {
      return dateString;
    }
  };
  
  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateString;
    }
  };
  
  // Get status badge
  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.scheduled;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };
  
  // Get appointment type badge
  const AppointmentTypeBadge = ({ type }) => {
    const config = APPOINTMENT_TYPE_CONFIG[type] || APPOINTMENT_TYPE_CONFIG.onsite;
    const Icon = config.icon;
    
    return (
      <Badge variant="outline" className={`${config.color} flex items-center gap-1 border`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="p-6 space-y-6" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            Termine
          </h1>
          <p className="text-muted-foreground">
            Übersicht aller Kundentermine
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Link to="/termine/erinnerungen">
            <Button variant="outline" data-testid="reminder-settings-btn">
              <Bell className="h-4 w-4 mr-2" />
              Erinnerungen
            </Button>
          </Link>
          <Button onClick={() => { fetchAppointments(); fetchStats(); }} variant="outline" data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => handleFilterChange("today")}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.today || 0}</div>
            <div className="text-sm text-muted-foreground">Heute</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => handleFilterChange("tomorrow")}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.tomorrow || 0}</div>
            <div className="text-sm text-muted-foreground">Morgen</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => handleFilterChange("week")}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.this_week || 0}</div>
            <div className="text-sm text-muted-foreground">Diese Woche</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => handleFilterChange("upcoming")}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.upcoming || 0}</div>
            <div className="text-sm text-muted-foreground">Kommend</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => handleFilterChange("cancelled")}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">{stats.cancelled || 0}</div>
            <div className="text-sm text-muted-foreground">Abgesagt</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => handleFilterChange("completed")}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.completed || 0}</div>
            <div className="text-sm text-muted-foreground">Abgeschlossen</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <form onSubmit={handleSearch} className="relative flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suche nach Kundenname..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary">Suchen</Button>
            </form>
            
            <Select value={filter} onValueChange={handleFilterChange}>
              <SelectTrigger className="w-full sm:w-[220px]" data-testid="filter-select">
                <SelectValue placeholder="Filter wählen" />
              </SelectTrigger>
              <SelectContent>
                {FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <opt.icon className="h-4 w-4" />
                      {opt.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : appointments.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">Keine Termine gefunden</h3>
              <p className="text-muted-foreground">
                {filter !== "all" 
                  ? "Keine Termine mit diesem Filter gefunden."
                  : "Es wurden noch keine Termine angelegt."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Terminart</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Uhrzeit</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Erinnerung</TableHead>
                  <TableHead>Mitarbeiter</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appointments.map((appointment) => (
                  <TableRow key={appointment.id} data-testid={`appointment-row-${appointment.id}`}>
                    <TableCell>
                      <AppointmentTypeBadge type={appointment.appointment_type} />
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatDate(appointment.date)}
                    </TableCell>
                    <TableCell>
                      {appointment.time || "-"} Uhr
                    </TableCell>
                    <TableCell>
                      <div>
                        <Link 
                          to={`/kunden/${appointment.customer_id}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {appointment.customer_name || `${appointment.customer_first_name} ${appointment.customer_last_name}`.trim() || "-"}
                        </Link>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {appointment.customer_email || "-"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={appointment.appointment_status} />
                    </TableCell>
                    <TableCell>
                      <ReminderStatusBadge 
                        status={appointment.appointment_reminder_status}
                        sentAt={appointment.appointment_reminder_sent_at}
                        size="small"
                      />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {appointment.created_by_name || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`actions-btn-${appointment.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDetailDialog(appointment)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Details & Historie
                          </DropdownMenuItem>
                          
                          {appointment.appointment_status !== "cancelled" && appointment.appointment_status !== "completed" && (
                            <>
                              <DropdownMenuItem onClick={() => openEditDialog(appointment)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Bearbeiten
                              </DropdownMenuItem>
                              
                              <DropdownMenuItem onClick={() => handleResendConfirmation(appointment)}>
                                <Send className="h-4 w-4 mr-2" />
                                Bestätigung erneut senden
                              </DropdownMenuItem>
                              
                              <DropdownMenuSeparator />
                              
                              <DropdownMenuItem onClick={() => handleCompleteAppointment(appointment)}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Als abgeschlossen markieren
                              </DropdownMenuItem>
                              
                              <DropdownMenuItem 
                                onClick={() => openCancelDialog(appointment)}
                                className="text-orange-600"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Absagen
                              </DropdownMenuItem>
                            </>
                          )}
                          
                          <DropdownMenuSeparator />
                          
                          <DropdownMenuItem 
                            onClick={() => { setDeletingAppointment(appointment); setDeleteDialogOpen(true); }}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        
        {/* Pagination */}
        {totalCount > 0 && (
          <div className="border-t p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground" data-testid="pagination-info">
                  {startItem}-{endItem} von {totalCount} Terminen
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Pro Seite:</span>
                  <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                    <SelectTrigger className="w-[80px] h-8" data-testid="page-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map(size => (
                        <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  data-testid="pagination-first"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-1 px-2">
                  <span className="text-sm font-medium" data-testid="pagination-current">
                    Seite {currentPage} von {totalPages || 1}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                  data-testid="pagination-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages || loading}
                  data-testid="pagination-last"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Termin bearbeiten
            </DialogTitle>
            <DialogDescription>
              {editingAppointment?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Warning about update email */}
            {editingAppointment?.email_sent && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                <AlertCircle className="h-4 w-4 inline mr-2" />
                Bei Änderung von Datum oder Uhrzeit wird der Kunde automatisch per E-Mail informiert.
              </div>
            )}
            
            {/* Appointment Type Selection */}
            <div className="space-y-2">
              <Label>Terminart</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={editForm.appointment_type === "onsite" ? "default" : "outline"}
                  className={`flex-1 ${editForm.appointment_type === "onsite" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                  onClick={() => setEditForm({ ...editForm, appointment_type: "onsite" })}
                >
                  <Home className="h-4 w-4 mr-2" />
                  Vor-Ort-Termin
                </Button>
                <Button
                  type="button"
                  variant={editForm.appointment_type === "phone" ? "default" : "outline"}
                  className={`flex-1 ${editForm.appointment_type === "phone" ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                  onClick={() => setEditForm({ ...editForm, appointment_type: "phone" })}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Telefontermin
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-date">Datum</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-time">Uhrzeit</Label>
                <Input
                  id="edit-time"
                  type="time"
                  value={editForm.time}
                  onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-duration">Dauer (Minuten)</Label>
              <Input
                id="edit-duration"
                type="number"
                value={editForm.duration_minutes}
                onChange={(e) => setEditForm({ ...editForm, duration_minutes: parseInt(e.target.value) || 60 })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-location">Ort / Hinweis</Label>
              <Input
                id="edit-location"
                value={editForm.location_note}
                onChange={(e) => setEditForm({ ...editForm, location_note: e.target.value })}
                placeholder="z.B. Eingang über Hinterhof"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Interne Notizen</Label>
              <Textarea
                id="edit-notes"
                value={editForm.message}
                onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                placeholder="Nur intern sichtbar"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveAppointment} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <XCircle className="h-5 w-5" />
              Termin absagen
            </DialogTitle>
            <DialogDescription>
              {cancellingAppointment?.customer_name} - {formatDate(cancellingAppointment?.date)} um {cancellingAppointment?.time} Uhr
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cancel-reason">Grund (optional)</Label>
              <Textarea
                id="cancel-reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="z.B. Krankheit, Terminkonflikt..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="send-cancel-email"
                checked={sendCancellationEmail}
                onCheckedChange={setSendCancellationEmail}
              />
              <Label htmlFor="send-cancel-email" className="text-sm">
                Absagemail an Kunden senden
              </Label>
            </div>
            
            {sendCancellationEmail && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <Mail className="h-4 w-4 inline mr-2" />
                Eine Absagemail wird an {cancellingAppointment?.customer_email} gesendet.
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleCancelAppointment} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Termin absagen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail/History Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Termindetails
            </DialogTitle>
            <DialogDescription>
              {selectedAppointment?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <StatusBadge status={selectedAppointment.appointment_status} />
                <div className="text-sm text-muted-foreground">
                  Erstellt: {formatDateTime(selectedAppointment.created_at)}
                </div>
              </div>
              
              {/* Details */}
              <Card>
                <CardContent className="p-4 grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Datum & Uhrzeit</div>
                    <div className="font-medium">{formatDate(selectedAppointment.date)}</div>
                    <div className="text-sm">{selectedAppointment.time} Uhr</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Kunde</div>
                    <div className="font-medium">{selectedAppointment.customer_name}</div>
                    <div className="text-sm">{selectedAppointment.customer_email}</div>
                  </div>
                  {selectedAppointment.customer_address && (
                    <div className="col-span-2">
                      <div className="text-sm text-muted-foreground">Adresse</div>
                      <div className="font-medium flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {selectedAppointment.customer_address}
                      </div>
                    </div>
                  )}
                  {selectedAppointment.location_note && (
                    <div className="col-span-2">
                      <div className="text-sm text-muted-foreground">Hinweis</div>
                      <div className="text-sm">{selectedAppointment.location_note}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-sm text-muted-foreground">Mitarbeiter</div>
                    <div className="font-medium">{selectedAppointment.created_by_name || "-"}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">E-Mail gesendet</div>
                    <div className="font-medium">{selectedAppointment.email_sent ? "Ja" : "Nein"}</div>
                  </div>
                  
                  {/* Erinnerungsstatus */}
                  <div className="col-span-2 pt-2 border-t">
                    <div className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                      <Bell className="h-4 w-4" />
                      Terminerinnerung
                    </div>
                    <div className="flex items-center gap-3">
                      <ReminderStatusBadge 
                        status={selectedAppointment.appointment_reminder_status}
                        sentAt={selectedAppointment.appointment_reminder_sent_at}
                      />
                      {selectedAppointment.appointment_reminder_sent_at && (
                        <span className="text-sm text-muted-foreground">
                          Gesendet am: {formatDateTime(selectedAppointment.appointment_reminder_sent_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* History */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Verlauf / Audit Trail
                </h3>
                {loadingEvents ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : appointmentEvents.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    Keine Ereignisse vorhanden
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-lg p-2">
                    {appointmentEvents.map((event, index) => (
                      <div 
                        key={event.id || index}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 border-b last:border-b-0"
                      >
                        <div className={`p-2 rounded-full ${
                          event.display?.color === "green" ? "bg-green-100" :
                          event.display?.color === "red" ? "bg-red-100" :
                          event.display?.color === "orange" ? "bg-orange-100" :
                          event.display?.color === "blue" ? "bg-blue-100" :
                          "bg-gray-100"
                        }`}>
                          {event.event_type === "created" && <Calendar className="h-4 w-4" />}
                          {event.event_type === "updated" && <Edit className="h-4 w-4" />}
                          {event.event_type === "rescheduled" && <RefreshCw className="h-4 w-4" />}
                          {event.event_type === "cancelled" && <XCircle className="h-4 w-4" />}
                          {event.event_type === "completed" && <CheckCircle className="h-4 w-4" />}
                          {event.event_type === "deleted" && <Trash2 className="h-4 w-4" />}
                          {event.event_type?.includes("email") && <Mail className="h-4 w-4" />}
                          {!["created", "updated", "rescheduled", "cancelled", "completed", "deleted"].includes(event.event_type) && !event.event_type?.includes("email") && <Clock className="h-4 w-4" />}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{event.display?.label || event.event_type}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatDateTime(event.timestamp)} • {event.user_name}
                          </div>
                          {event.details?.changes && Object.keys(event.details.changes).length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {Object.entries(event.details.changes).map(([key, val]) => (
                                <div key={key}>
                                  {key}: {val.old} → {val.new}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Email History */}
              {selectedAppointment.email_history && selectedAppointment.email_history.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    E-Mail-Verlauf
                  </h3>
                  <div className="space-y-2 border rounded-lg p-2">
                    {selectedAppointment.email_history.map((email, index) => (
                      <div key={email.id || index} className="flex items-center justify-between p-2 hover:bg-muted/50 rounded">
                        <div>
                          <div className="text-sm font-medium">{email.event_type}</div>
                          <div className="text-xs text-muted-foreground">{email.recipient}</div>
                        </div>
                        <Badge className={
                          email.status === "delivered" ? "bg-green-100 text-green-800" :
                          email.status === "bounced" ? "bg-red-100 text-red-800" :
                          email.status === "sent" ? "bg-blue-100 text-blue-800" :
                          "bg-gray-100 text-gray-800"
                        }>
                          {email.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Termin löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Termin mit {deletingAppointment?.customer_name} am {formatDate(deletingAppointment?.date)} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAppointment} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
